/**
 * Qué puede HACER el cliente cuando el cotizador de envío falla — espejo de
 * `deliveryErrorUx.ts` (léelo primero: mismas dos reglas, mismo motivo).
 *
 * La diferencia con delivery: `/api/shipping/quote` NO devuelve un único
 * status por familia de error. `state_not_served` / `below_min_parcels` /
 * `address_not_verified` son 200 con un `error` en el body (decisiones de
 * negocio, no fallos HTTP); `not_shippable_items` / `too_many_parcels` son
 * 409; `rate_limited` / `daily_quota_exhausted` son 429; todo lo demás
 * (incluido un 404 `shipping_disabled` — el local apagó el riel a mitad del
 * checkout) cae a `quote_failed`. `shipErrorKindFromResponse` es la ÚNICA
 * traducción status+body → kind — no reimplementar este mapeo en el
 * componente.
 *
 * `quote_in_use` no lo emite `/api/shipping/quote`: es un rechazo de
 * `/api/online-orders/session` (409, el mismo envío se está procesando en
 * otra sesión ahora mismo) que PaymentStep alimenta a mano. M4 (ola final de
 * fixes): el copy es neutral a propósito — no afirma quién lo está pagando.
 */

export type ShipErrorKind =
  | 'state_not_served'
  | 'below_min_parcels'
  | 'address_not_verified'
  | 'not_shippable_items'
  | 'too_many_parcels'
  | 'rate_limited'
  | 'daily_quota_exhausted'
  | 'quote_in_use'
  | 'not_available'
  | 'network'
  | 'quote_failed'

export type ShipErrorUx = {
  /** `warn` = cambió el plan, esto no se arregla solo. `neutral` = transitorio. */
  tone: 'warn' | 'neutral'
  canRetry: boolean
  /** Cambiar la dirección puede arreglarlo (otro estado, otra dirección verificable). */
  canChangeAddress: boolean
}

export const SHIP_ERROR_UX: Record<ShipErrorKind, ShipErrorUx> = {
  // Otra dirección (otro estado) sí puede servir — no es un "nunca" del local.
  state_not_served: { tone: 'warn', canRetry: false, canChangeAddress: true },
  // Política de carrito, no de dirección: agregar más al carrito es lo que
  // arregla esto, no reintentar ni cambiar la dirección.
  below_min_parcels: { tone: 'warn', canRetry: false, canChangeAddress: false },
  // EasyPost no pudo verificar lo tecleado — cambiar la dirección es la salida real.
  address_not_verified: { tone: 'warn', canRetry: false, canChangeAddress: true },
  // Problema del CARRITO (un producto sin datos de envío completos), no de la
  // dirección — en teoría no debería pasar porque shipModeAvailable ya filtra
  // el carrito client-side, pero un estado stale puede llegar hasta acá.
  not_shippable_items: { tone: 'warn', canRetry: false, canChangeAddress: false },
  too_many_parcels: { tone: 'warn', canRetry: false, canChangeAddress: false },
  // Transitorios de VERDAD: esperar y reintentar ES la acción correcta.
  rate_limited: { tone: 'neutral', canRetry: true, canChangeAddress: false },
  daily_quota_exhausted: { tone: 'warn', canRetry: false, canChangeAddress: false },
  // Otra pestaña está pagando el MISMO quote: esperar y volver a intentar
  // (re-cotizar) es razonable, cambiar dirección no aporta nada.
  quote_in_use: { tone: 'neutral', canRetry: true, canChangeAddress: false },
  // El local apagó el riel (o la config quedó incompleta) a mitad del checkout.
  not_available: { tone: 'warn', canRetry: false, canChangeAddress: false },
  network: { tone: 'neutral', canRetry: true, canChangeAddress: false },
  quote_failed: { tone: 'warn', canRetry: false, canChangeAddress: true },
}

/**
 * status + body de `/api/shipping/quote` → kind. Único punto de traducción —
 * ver app/api/shipping/quote/route.ts para los códigos exactos.
 */
export function shipErrorKindFromResponse(
  status: number,
  body: { error?: string } | null,
): ShipErrorKind {
  const code = body?.error
  if (status === 200 && code === 'state_not_served') return 'state_not_served'
  if (status === 200 && code === 'below_min_parcels') return 'below_min_parcels'
  if (status === 200 && code === 'address_not_verified') return 'address_not_verified'
  if (status === 404) return 'not_available'
  if (status === 409 && code === 'not_shippable_items') return 'not_shippable_items'
  // Ronda de fix 1, MINOR-a: un 409 con un código QUE NO reconocemos ya no
  // asume `too_many_parcels` — el route tiene DOS motivos distintos para 409
  // (not_shippable_items / too_many_parcels) y adivinar uno de los dos para
  // un código nuevo/desconocido es activamente engañoso (el cliente vería
  // "reduce la cantidad" cuando el problema real podría ser otro). Cae al
  // fallback conservador, como el resto de statuses no reconocidos.
  if (status === 409 && code === 'too_many_parcels') return 'too_many_parcels'
  if (status === 429 && code === 'daily_quota_exhausted') return 'daily_quota_exhausted'
  if (status === 429) return 'rate_limited'
  return 'quote_failed'
}

/**
 * Código de error → CLAVE i18n (namespace `public.onlineOrder.checkout`) —
 * NUNCA el código crudo, y NUNCA texto hardcodeado acá.
 *
 * Ronda de fix 1 (code review), IMPORTANT-3: la versión anterior de esta
 * función devolvía el texto en español DIRECTO, y era la única fuente de
 * copy que usaba `PaymentStep.tsx` para los rechazos de envío de
 * `/api/online-orders/session` — un cliente con la web en inglés veía
 * español en la pantalla de pago. `deliveryErrorUx.ts` (el espejo de este
 * archivo) nunca tuvo ese problema porque NUNCA trae copy propio: solo
 * devuelve la política (tono/retry/cambiar dirección) y el COMPONENTE
 * resuelve el texto con su propio `useTranslations`. Esta función sigue esa
 * misma regla — devuelve la clave, el llamador la resuelve.
 *
 * Cubre los códigos reales de `/api/shipping/quote` (T8) y de la rama de
 * envío de `/api/online-orders/session` (T9/T10: `quote_in_use` es el 409 de
 * "este envío se está procesando en otra sesión ahora mismo" — copy neutral,
 * M4). `below_min_parcels` necesita el parámetro `{min}` al interpolar —
 * quien llama se lo pasa a `t(key, { min })`, esta función no lo toca.
 */
export function shipErrorMessageKey(code: string): string {
  switch (code) {
    case "state_not_served":
      return "shipStateNotServedBody"
    case "below_min_parcels":
      return "shipBelowMinParcelsBody"
    case "address_not_verified":
      return "shipAddressNotVerifiedBody"
    case "not_shippable_items":
      return "shipNotShippableItemsBody"
    case "too_many_parcels":
      return "shipTooManyParcelsBody"
    case "rate_limited":
      return "shipRateLimitedBody"
    case "daily_quota_exhausted":
      return "shipDailyQuotaBody"
    case "quote_in_use":
      return "shipQuoteInUseBody"
    case "not_available":
      return "shipNotAvailableBody"
    default:
      return "shipQuoteFailedBody"
  }
}
